import React from "react";
import { motion } from "framer-motion";

const Policy = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-inter font-bold text-acBlack leading-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Privacy Policy
          </motion.h1>
          <motion.p 
            className="text-gray-600"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Last updated: 16th August 2024
          </motion.p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            className="prose prose-gray max-w-none"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {/* Introduction */}
            <div className="mb-12">
              <p className="text-gray-700 leading-relaxed">
                This Privacy Policy explains how Validate Systems ("Validate Systems," "we," "us," "our") collects, uses, discloses, and protects personal data when we provide our services and when you interact with our website, forms, or communications.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                By using our services or site, you agree to this Policy. If you do not agree, please do not use our services.
              </p>
            </div>

            {/* Section 1 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">1. Who we are & scope</h2>
              <div className="space-y-4 text-gray-700">
                <p><strong>Controller/Processor roles.</strong> Validate Systems generally acts as a data controller for personal data processed in connection with our Verification services and our Travel & Immigration advisory/documentation support. When we process personal data on documented instructions from a business client (e.g., an employer commissioning checks on candidates), we may act as a data processor for that client.</p>
                <p><strong>Services covered:</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Verification (human-led checks):</strong> identity/background, employment & education confirmation, address verification (field visits), guarantor/corporate due diligence, and financial standing evidence (consent-based).</li>
                  <li><strong>Travel & Immigration (advisory & documentation support):</strong> route guidance, personalized checklists & templates, background certificates coordination via our Verification team, application organization & admin QA, appointment support, and interview practice. We do not provide legal advice or legal representation.</li>
                </ul>
              </div>
            </div>

            {/* Section 2 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">2. Personal data we collect</h2>
              <div className="space-y-6 text-gray-700">
                <div>
                  <p className="font-semibold mb-2">2.1 Data you provide directly</p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>Contact details (name, email), organization/role, nationality, country of residence.</li>
                    <li>Case information (objectives, route/destination, timeline, prior refusals).</li>
                    <li>Identification documents (ID/passport details, photos) where required and consented.</li>
                    <li>Employment/education documents (CVs, letters, transcripts).</li>
                    <li>Address details and proofs (utility, tenancy, landlord contact where applicable).</li>
                    <li>Financial evidence (bank letters, income summaries, asset proofs) with explicit consent.</li>
                    <li>Communications (emails, messages, attachments).</li>
                    <li>Consent records and acknowledgements (e.g., "I have authority to request this check").</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-2">2.2 Data we collect or confirm from third parties (with consent or lawful basis)</p>
                  <p>Confirmations from employers/HR, registrars/schools, landlords/neighbors, banks, references/guarantors, professional bodies, registries, and public sources.</p>
                  <p className="mt-2">For address checks: field evidence such as photos, geolocation and timestamps (where permitted), neighbor/landlord corroboration notes.</p>
                </div>

                <div>
                  <p className="font-semibold mb-2">2.3 Website/technical data</p>
                  <p>Device, browser, and interaction data (e.g., IP address, pages viewed, timestamps). Cookies and similar technologies (see Section 12).</p>
                </div>

                <div>
                  <p className="font-semibold mb-2">2.4 Special category data (rare)</p>
                  <p>We do not intentionally seek sensitive data (e.g., health, religion). If a visa route requires limited sensitive data (e.g., medical exam proof), we process only what is necessary, with explicit consent where required and subject to enhanced safeguards.</p>
                </div>

                <div>
                  <p className="font-semibold mb-2">2.5 Children's data</p>
                  <p>Our services are for adults (18+). We do not knowingly collect personal data of children. If you believe a child's data has been provided to us, contact legal@validatesystems.com.</p>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">3. Sources of data</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Directly from you (forms, emails, uploads).</li>
                <li>From business clients who authorize checks.</li>
                <li>From third parties we contact as sources-of-truth (with permission/lawful basis).</li>
                <li>From publicly available records and official registries.</li>
                <li>From service providers and technology tools we use to operate our services.</li>
              </ul>
            </div>

            {/* Section 4 */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">4. Why we process personal data (purposes)</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Service delivery (Verification):</strong> Conduct human-led checks, obtain evidence, compile signed reports with outcome labels (Verified / Inconclusive / Discrepant).</li>
                <li><strong>Service delivery (Travel & Immigration):</strong> Provide advisory and documentation support (eligibility guidance, checklists, templates, assembly, admin QA, mock interviews, appointment support, gap analysis).</li>
                <li><strong>Consent & authority management:</strong> Record and validate permissions to proceed.</li>
                <li><strong>Security & integrity:</strong> Prevent fraud, maintain chain-of-custody, and detect misuse.</li>
                <li><strong>Client support & communications:</strong> Respond to queries, schedule appointments, share deliverables, and provide status updates.</li>
                <li><strong>Business operations:</strong> Invoicing, accounting, vendor management, audits.</li>
                <li><strong>Legal & compliance:</strong> Respond to lawful requests, enforce agreements, maintain records.</li>
              </ul>
            </div>

            {/* Continue with remaining sections following the same pattern... */}
            
            {/* Section 10 - Your Rights */}
            <div className="mb-10">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">10. Your privacy choices & rights</h2>
              <div className="space-y-4 text-gray-700">
                <p>Subject to applicable law, you may have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access your personal data and obtain a copy.</li>
                  <li>Rectify inaccurate or incomplete data.</li>
                  <li>Delete/erase data in certain circumstances.</li>
                  <li>Restrict or object to processing.</li>
                  <li>Port data (receive it in a structured, commonly used format).</li>
                  <li>Withdraw consent where processing is based on consent (this does not affect prior lawful processing).</li>
                </ul>
                <p className="font-semibold mt-4">How to exercise your rights</p>
                <p>Email legal@validatesystems.com with:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Your full name and a clear description of your request.</li>
                  <li>The service/context (e.g., Verification case ID if known).</li>
                  <li>Proof of identity (we may ask for additional verification to protect your data).</li>
                </ul>
                <p className="mt-2">We will respond within a reasonable timeframe under applicable law. You may also lodge a complaint with your local data protection authority.</p>
              </div>
            </div>

            {/* Section 16 - Contact */}
            <div className="mb-10 bg-gray-50 rounded-lg p-8">
              <h2 className="text-2xl font-inter font-bold text-acBlack mb-4">16. Contact us</h2>
              <p className="text-gray-700 mb-4">Questions about this Privacy Policy or your personal data?</p>
              <p className="text-gray-700">
                <strong>Email:</strong> <a href="mailto:legal@validatesystems.com" className="text-acYellow hover:text-green-700 transition-colors">legal@validatesystems.com</a>
              </p>
            </div>

          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Policy;